import { get_token } from "@/router";
import SettingsUserManagementAlerts from "./SettingsUserManagementAlerts/SettingsUserManagementAlerts.vue";
import SettingsUserManagementAddUser from "./SettingsUserManagementAddUser/SettingsUserManagementAddUser.vue";
import SettingsUserManagementHeader from "./SettingsUserManagementHeader/SettingsUserManagementHeader.vue";
import SettingsUserManagementTable from "./SettingsUserManagementTable/SettingsUserManagementTable.vue";
import {
    ensure_trailing_slash,
    normalize_properties_response
} from "@/utils/api_utils";
import { filter_live_active_properties } from "@/utils/property_access_utils";
import {
    fetch_user_property_assignments,
    get_assignment_properties,
    get_assignment_property_ids
} from "@/utils/user_property_assignments";
import {
    get_auth_headers,
    get_fetch_error_message,
    get_user_friendly_error_message,
    get_response_data,
    require_array_response,
    roles_url,
    users_list_url,
    users_url
} from "./settings_user_management_api";
import { get_session_policy_label } from "@/utils/session_policy";

const properties_url = ensure_trailing_slash(process.env.VUE_APP_PROPERTIES_URL);

function get_table_helper() {
    return window.global_data_table || null;
}

function get_settings_user_table_helpers() {
    const selector = ".global_data_table";

    return ({
        watch: {},
        methods: {
            initialize_tables_when_ready() {
                this.$nextTick(() => {
                    get_table_helper()?.init_all_tables?.(this.$el, selector);
                });
            },

            destroy_tables() {
                get_table_helper()?.destroy_all_tables?.(this.$el, selector);
            },

            rebuild_tables() {
                this.$nextTick(() => {
                    get_table_helper()?.rebuild_all_tables?.(this.$el, selector);
                });
            }
        }
    });
}

const settings_user_table_helpers = get_settings_user_table_helpers();

export default {
    name: "SettingsUserManagementTab",

    components: {
        SettingsUserManagementAlerts,
        SettingsUserManagementAddUser,
        SettingsUserManagementHeader,
        SettingsUserManagementTable
    },

    props: {
        user_profile: {
            type: Object,
            default: null
        }
    },

    data() {
        return ({
            users: [],
            role_options: [],
            property_options: [],
            is_loading: false,
            is_loading_roles: false,
            is_loading_properties: false,
            load_error: "",
            save_success: "",
            show_add_user_modal: false,
            users_url: users_url,
            users_list_url: users_list_url,
            roles_url: roles_url,
            properties_url: properties_url
        });
    },

    computed: {
        active_users_count() {
            return this.users.filter((user) => user.is_active).length;
        },

        current_user_id() {
            return this.user_profile?.id || null;
        },

        current_user_email() {
            return String(this.user_profile?.email || "").toLowerCase();
        },

        has_users_endpoint() {
            return Boolean(this.users_url);
        }
    },

    watch: {
        ...(settings_user_table_helpers.watch || {}),
        user_profile: {
            deep: true,
            handler(next_profile) {
                if (next_profile && !this.users.length && !this.is_loading) {
                    this.set_users(this.get_fallback_users());
                }
            }
        }
    },

    async mounted() {
        await this.load_initial_data();
    },

    beforeUnmount() {
        settings_user_table_helpers.beforeUnmount?.call(this);
    },

    methods: {
        ...(settings_user_table_helpers.methods || {}),

        get_role_name(user) {
            return user?.user_role?.role?.name || "";
        },

        get_user_role_id(user) {
            return user?.user_role?.role?.id ?? null;
        },

        get_user_row(user) {
            const role = user?.user_role?.role || user?.role || null;
            const profile = user?.profile || user?.user_profile || {};
            const assigned_properties = this.get_assigned_properties(user);
            const property_ids = assigned_properties.map((property) => property.id).filter(Boolean);

            return ({
                id: user?.id ?? "",
                first_name: user?.first_name ?? "",
                last_name: user?.last_name ?? "",
                username: user?.username ?? "",
                email: user?.email ?? "",
                phone_number: profile?.phone_number ?? user?.phone_number ?? "",
                session_policy: profile?.session_policy ?? user?.session_policy ?? "default",
                session_timeout_minutes: profile?.session_timeout_minutes ?? user?.session_timeout_minutes ?? null,
                session_label: get_session_policy_label(user),
                role: typeof role === "string" ? role : role?.name || this.get_role_name(user),
                role_id: typeof role === "object" ? role?.id ?? this.get_user_role_id(user) : this.get_user_role_id(user),
                property_ids,
                assigned_properties,
                is_active: user?.is_active !== false,
                last_login: user?.last_login || user?.lastLogin || "",
                raw: user
            });
        },

        async enrich_user_with_property_assignments(user) {
            if (!user?.id) {
                return user;
            }

            const assignments = await fetch_user_property_assignments(user.id, get_auth_headers(false), {
                include_inactive: true
            });
            const live_property_ids = this.property_options.map((property) => String(property.id));
            const active_assignments = assignments.filter((assignment) => {
                return assignment.is_active && live_property_ids.includes(String(assignment.property_id));
            });

            return ({
                ...user,
                property_assignments: assignments,
                property_ids: get_assignment_property_ids(active_assignments),
                assigned_properties: get_assignment_properties(active_assignments, this.property_options)
            });
        },

        get_assigned_properties(user) {
            const raw_properties = user?.assigned_properties
                || user?.properties
                || user?.property_assignments
                || user?.profile?.properties
                || [];
            const raw_property_ids = user?.property_ids
                || user?.assigned_property_ids
                || user?.profile?.property_ids
                || [];
            const properties = Array.isArray(raw_properties) ? raw_properties : [];
            const property_ids = Array.isArray(raw_property_ids) ? raw_property_ids : [];
            const normalized_from_properties = properties
                .map((property) => {
                    if (typeof property === "number" || typeof property === "string") {
                        return this.find_property_option(property) || { id: property, name: String(property) };
                    }

                    return ({
                        id: property?.property_id ?? property?.id ?? "",
                        name: property?.name || property?.property_name || property?.title || "Unnamed Site"
                    });
                })
                .filter((property) => property.id !== "" && property.name);
            const normalized_from_ids = property_ids
                .map((property_id) => this.find_property_option(property_id) || { id: property_id, name: String(property_id) })
                .filter((property) => property.id !== "" && property.name);

            return [...normalized_from_properties, ...normalized_from_ids]
                .filter((property, index, all_properties) => {
                    return all_properties.findIndex((existing_property) => String(existing_property.id) === String(property.id)) === index;
                })
                .filter((property) => {
                    return this.property_options.some((property_option) => String(property_option.id) === String(property.id));
                });
        },

        find_property_option(property_id) {
            return this.property_options.find((property) => String(property.id) === String(property_id)) || null;
        },

        get_fallback_users() {
            if (!this.user_profile) {
                return [];
            }

            return [this.get_user_row(this.user_profile, "current-user")];
        },

        get_fallback_role_options() {
            const current_user_role = this.user_profile?.user_role?.role || null;
            return current_user_role ? [current_user_role] : [];
        },

        async load_initial_data() {
            await this.load_roles();
            await this.load_properties();
            await this.load_users();
        },

        async load_roles() {
            this.is_loading_roles = true;

            try {
                const auth_token = get_token();

                if (!this.roles_url || !auth_token) {
                    if (!this.roles_url) {
                        console.warn("VUE_APP_ROLES_URL is not configured.");
                    }
                    this.role_options = this.get_fallback_role_options();
                    return;
                }

                const response = await fetch(this.roles_url, {
                    method: "GET",
                    headers: get_auth_headers(false)
                });
                const roles_data = await get_response_data(response, "Unable to load roles.");
                const roles = require_array_response(roles_data, "Roles response was not an array.");

                this.role_options = Array.isArray(roles) && roles.length
                    ? roles
                    : this.get_fallback_role_options();
            } catch {
                this.role_options = this.get_fallback_role_options();
            } finally {
                this.is_loading_roles = false;
            }
        },

        async load_users() {
            this.is_loading = true;
            this.load_error = "";

            try {
                const auth_token = get_token();

                if (!this.users_list_url || !auth_token) {
                    this.load_error = !this.users_list_url
                        ? "User list loading is not fully set up yet. Showing the profile information we have."
                        : "Your session needs a quick refresh. Showing the profile information we have for now.";
                    this.set_users(this.get_fallback_users(), false);
                    return;
                }

                const response = await fetch(this.users_list_url, {
                    method: "GET",
                    headers: get_auth_headers(false)
                });
                const users_data = await get_response_data(response, "Unable to load users.");
                const users = require_array_response(users_data, "Users response was not an array.");

                const users_with_assignments = await Promise.all(users.map((user) => this.enrich_user_with_property_assignments(user)));
                const next_users = users_with_assignments.length
                    ? users_with_assignments.map((user, index) => this.get_user_row(user, `local-${index + 1}`))
                    : [];

                this.set_users(next_users.length ? next_users : this.get_fallback_users(), false);
            } catch (error) {
                this.set_users(this.get_fallback_users(), false);
                this.load_error = `${get_fetch_error_message(error, "We could not load the full user list.")} Showing the profile information we have for now.`;
            } finally {
                this.is_loading = false;
                this.$nextTick(() => {
                    this.rebuild_tables();
                });
            }
        },

        async load_properties() {
            this.is_loading_properties = true;

            try {
                const auth_token = get_token();

                if (!this.properties_url || !auth_token) {
                    this.property_options = [];
                    return;
                }

                const response = await fetch(this.properties_url, {
                    method: "GET",
                    headers: get_auth_headers(false)
                });
                const properties_data = await get_response_data(response, "Unable to load properties.");
                const properties = require_array_response(properties_data, "Properties response was not an array.");

                this.property_options = filter_live_active_properties(normalize_properties_response(properties));
            } catch {
                this.property_options = [];
            } finally {
                this.is_loading_properties = false;
            }
        },

        set_users(next_users, should_rebuild = true) {
            this.destroy_tables();
            this.users = next_users;

            if (should_rebuild) {
                this.$nextTick(() => {
                    this.rebuild_tables();
                });
            }
        },

        open_add_user() {
            this.load_error = "";
            this.save_success = "";
            this.show_add_user_modal = true;
        },

        close_add_user_modal() {
            this.show_add_user_modal = false;
        },

        async handle_reload_users(done) {
            await this.load_users();

            if (typeof done === "function") {
                done();
            }
        },

        handle_user_added(message, local_user = null) {
            if (local_user) {
                this.set_users([...this.users, local_user]);
            }

            this.save_success = message || "User added successfully.";
            this.load_error = "";
        },

        set_load_error(message) {
            this.load_error = message ? get_user_friendly_error_message(message) : "";

            if (message) {
                this.save_success = "";
            }
        },

        set_save_success(message) {
            this.save_success = message || "";

            if (message) {
                this.load_error = "";
            }
        },

        clear_load_error() {
            this.load_error = "";
        },

        clear_save_success() {
            this.save_success = "";
        }
    }
};
